import { useCallback, useRef, useMemo, useState } from 'react';

import { RegexValidators } from '@libs/validations';

import { ResultFeedbackType } from 'src/constants';
import { useChangePhone, useTranslation } from 'src/hooks';
import { AccountInfoModalProps } from 'src/types';

import ChangePhoneForm from '../change-phone-form/ChangePhoneForm';

import { CustomModalWithTwoFa, CustomResultFeedback } from './styles';

const ChangePhoneModal: React.FC<AccountInfoModalProps> = ({
  onCancel,
  initialValue,
  shouldRender,
  forceNewNumber,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const [shouldShowContent, setShouldShowContent] = useState(true);

  const showContent = () => {
    setShouldShowContent(true);
  };
  const hideContent = () => {
    setShouldShowContent(false);
  };

  const {
    code,
    formErrors,
    feedbackType,
    errorMessage,
    phoneToDisplay,
    touched,
    values,
    isValid,
    handlePhoneChange,
    sendCodeHandler,
    handleSubmit,
    handleChange,
    isSameNumber,
    clearCode,
    setCode,
    countDown,
    isCounterRunning,
    counterInterval,
    isButtonActive,
    phoneNumber,
  } = useChangePhone(showContent, hideContent, initialValue, forceNewNumber);

  const changePhone = () => handlePhoneChange(values);

  const description = useMemo(() => {
    if (feedbackType === ResultFeedbackType.SUCCESS) {
      return phoneNumber ? 'success_phone_change' : 'success_phone_add';
    } else {
      return errorMessage || 'error_phone_change';
    }
  }, [feedbackType, phoneNumber, errorMessage]);

  const sendCode = useCallback(
    (phone?: string) => {
      if (phone) {
        sendCodeHandler(phone, RegexValidators.IS_PHONE_VALID.test(phone));
      }
    },
    [sendCodeHandler]
  );

  const modalTitle = useRef(
    phoneNumber ? 'phone_change_title' : 'phone_add_title'
  ).current;

  return (
    <CustomModalWithTwoFa
      shouldShowContent={shouldShowContent}
      testId="SecurityNumberModal"
      title={t(modalTitle)}
      onAuthFinish={changePhone}
      onModalClose={onCancel}
      {...modalProps}
    >
      {feedbackType && onCancel ? (
        <CustomResultFeedback
          onClick={onCancel}
          type={feedbackType}
          description={t(description)}
        />
      ) : (
        <ChangePhoneForm
          setCode={setCode}
          clearCode={clearCode}
          formErrors={formErrors}
          sendCode={sendCode}
          errorMessage={errorMessage}
          handleSubmit={handleSubmit}
          values={values}
          handleChange={handleChange}
          touched={touched}
          isValid={isValid}
          isSameNumber={isSameNumber}
          phoneToDisplay={phoneToDisplay}
          code={code}
          onCancel={onCancel}
          countDown={countDown}
          isCounterRunning={isCounterRunning}
          initialValue={initialValue}
          handlePhoneChange={handlePhoneChange}
          isButtonActive={isButtonActive}
          counterInterval={counterInterval}
          shouldRender={shouldRender}
        />
      )}
    </CustomModalWithTwoFa>
  );
};
export default ChangePhoneModal;
